/* GlossaryTooltip — tooltip inline para siglas e termos técnicos.
 *
 * Uso:
 *   <GlossaryTooltip term="FPF">FPF</GlossaryTooltip>
 *
 * - Busca o significado em `lib/glossary.js` (case-insensitive).
 * - Renderiza um Popover do shadcn (funciona em hover, click e teclado/foco).
 * - Adiciona underline tracejado discreto para indicar que é interativo.
 * - Acessível: trigger é <button>, com aria-label automático e foco visível.
 */
import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { findTerm } from "@/lib/glossary";

export default function GlossaryTooltip({ term, children, className = "" }) {
  const [open, setOpen] = useState(false);
  const data = findTerm(term);

  if (!data) {
    return <span className={className}>{children ?? term}</span>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          aria-label={`Definição: ${data.title}`}
          data-testid={`glossary-tooltip-${data.key.toLowerCase().replace(/\s+/g, "-")}`}
          className={`inline-flex items-center underline decoration-dotted decoration-slate-400 underline-offset-2 hover:decoration-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-400/40 rounded-sm ${className}`}
        >
          {children ?? term}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        sideOffset={6}
        className="max-w-xs w-[300px] text-sm leading-relaxed p-3"
        role="tooltip"
      >
        <div className="font-display font-semibold text-[#0F1B2D] dark:text-slate-100 text-[13px] mb-1.5">
          {data.title}
        </div>
        <div className="text-[12.5px] text-slate-600 dark:text-slate-300 leading-snug">
          {data.short}
        </div>
      </PopoverContent>
    </Popover>
  );
}
